import { lazy } from "react";
import { Suspense } from "react";
import CardCostomosted from "./CardCostomosted.js"; // adjust path

function withCardWrapper(LazyComponent, cardData) {
  return function Wrapped(props) {
    return (
      <CardCostomosted cardData={cardData}>
        <Suspense fallback={null}>
          <LazyComponent {...props} />
        </Suspense>
      </CardCostomosted>
    );
  };
}

/**
 * moduleRegistry.js
 *
 * THE single source of truth mapping a module id (a plain string that
 * comes straight out of the login response, e.g. "appointments") to:
 *   - a human label (for the sidebar)
 *   - a route path (for the router)
 *   - a lazily-loaded page component
 *
 * Bootstrap, the Router and the Sidebar all read from this registry.
 * None of them contain "if module === 'appointments'" branching —
 * they just loop over whatever module ids the system config lists and
 * look each one up here. Adding module #6 means adding one entry here,
 * nothing else in the app changes.
 */
export const MODULE_REGISTRY = {
  //comen use 
  acceuil: {
    id: "acceuil",
    label: "Acceuil",
    path: "ma",
    icon: "house",
    withoutCardWrapper: true,
    component: lazy(() => import("./Main/mainPageOne.js")),
  },
  demmandes: {
    id: "demmandes",
    label: {
      default : "Demmandes",
      docteur : "Rendy-vous",
      gym : "Souscription",
      garderie : "Inscription"
    },
    path: "rq",
    icon: "calendar2-check",
    component: withCardWrapper(lazy(() => import("./Requests/requestPage.js")), { title: "test", back: "/cl" }),
    submodules: [
      {
        id: "reqinfo",
        label: "info",
        path: "info/:CID",
        component: withCardWrapper(lazy(() => import("./Requests/requestInfo.js")), { title: "test", back: "/cl" }),
      },
    ],
  },
  client: {
    id: "client",
    label: {
      default : "Client",
      docteur : "Patient",
      garderie : "Eleves",
      gym : "Membres",
      autoecole : "Condidat",
    }, 
    path: "cl",
    icon: "people",
    component: lazy(() => import("./Patient/patientPage.js")), 
    submodules: [
      {
        id: "ajouter",
        label: "Info facture",
        path: "ajouter",
        component:  lazy(() => import("./Patient/ajouterPatient.js")),
      },
      {
        id: "info",
        label: "Info facture",
        path: "info/:CID",
        component:  lazy(() => import("./Patient/patientInfo.js")),
      },
    ],
  },

  //docteur 
  ajouter_patient: {
    id: "ajouter_patient",
    label: "Ajouter Patient",
    path: "ajouter-pa",
    icon: "person-plus",
    component:  lazy(() => import("./docteur/Patients/ajouterPatient.js")),
    withCardWrapper: true,
    cardData: { title: "test", back: "/cl" },
  },
  rendy_vous: {
    id: "rendy_vous",
    label: "Rendy-Vous",
    path: "rendy-vous",
    icon: "calendar-check",
    component: lazy(() => import("./docteur/RendyVous/rendyVousPage.js")),
  },
  calendrier: {
    id: "calendrier",
    label: {
      default : "Calendrier",
      autoecole : "Examen",
      gym : "Souscription",
      garderie : "Inscription"
    },
    path: "calendrier",
    icon: "calendar-week",
    withCardWrapper: true,
    cardData: { title: "test", back: "/cl" },
    component: lazy(() => import("./docteur/Calendrier/recette.js")),
  },

 //autoecole
 seance_autoecole: {
    id: "seance_autoecole",
    label: "Seances",
    path: "sa",
    icon: "hourglass-split",
    component: lazy(() => import("./autoecole/Seances/nouveauxSeance.js")),
  },
  my_seance_autoecole: {
    id: "my_seance_autoecole",
    label: "My Seances",
    path: "ms",
    icon: "folder2-open",
    component: lazy(() => import("./autoecole/Seances/SeancesListe.js")),
  },
  trajets: {
    id: "trajets",
    label: "Trajets",
    path: "tr",
    icon: "map",
    component: lazy(() => import("./autoecole/Trajets/trajetPage.js")),
  },

  //garderie
  seance_garderie: {
    id: "seance_garderie",
    label: "Seances",
    path: "sa",
    icon: "hourglass-split",
    component: lazy(() => import("./garderie/Seances/nouveauxSeance.js")),
  },
  my_seance_garderie: {
    id: "my_seance_garderie",
    label: "My Seances",
    path: "ms",
    icon: "journal-bookmark-fill",
    component: lazy(() => import("./garderie/Journale/journaleListe.js")),
  },
  appel: {
    id: "appel",
    label: "Appel",
    path: "ap",
    icon: "people",
    component: lazy(() => import("./garderie/Seances/appealPage.js")),
  },
  emploi: {
    id: "emploi",
    label: "Emploi",
    path: "mp",
    icon: "calendar-week",
    component: lazy(() => import("./garderie/emploi/emploiPage.js")),
  },
  examen: {
    id: "examen",
    label: "Examen",
    path: "ex",
    icon: "bag-check-fill",
    component: lazy(() => import("./garderie/Examen/examainPage.js")),
  },

  //gym
  seance_gym: {
    id: "seance_gym",
    label: "Nouveaux Seances",
    path: "sa",
    icon: "hourglass-split",
    component: lazy(() => import("./gym/Seances/nouveauxSeance.js")),
  },
  abonnement: {
    id: "abonnement",
    label: "Abonnement",
    path: "ab",
    icon: "credit-card-fill",
    component: lazy(() => import("./gym/Abonemment/nouveauxAbonnemment.js")),
  },
  renouvellement_abonnement: {
    id: "renouvellement_abonnement",
    label: "Renouvellment",
    path: "re",
    icon: "arrow-repeat",
    component: lazy(() => import("./gym/Abonemment/renouvelemment.js")),
  },

  //restaurant 
  caisses_restaurant: {
    id: "caisses",
    label: "Caisses",
    path: "ca",
    icon: "pc-display-horizontal",
    component: lazy(() => import("./restaurant/Caisse/caisseSimple.js")),
    submodules : [
        {
          id: "caisses",
          label: "Caisse Normale",
          path: "normale",
          icon: "pc-display-horizontal",
          component: lazy(() => import("./restaurant/Caisse/caisseSimple.js")),
        },
        {
          id: "caisses",
          label: "Caisse Code à Barre",
          path: "barre-code",
          icon: "upc-scan",
          component: lazy(() => import("./restaurant/Caisse/caisseCodeBar.js")),
        },
        {
          id: "caisses",
          label: "Caisse Stock",
          path: "stock",
          icon: "box2-heart-fill",
          component: lazy(() => import("./restaurant/Caisse/caisseStock.js")),
        },
        
    ]
  },
  facture_restaurant: {
    id: "facture",
    label: "Facture",
    path: "ft",
    icon: "receipt-cutoff",
    component: lazy(() => import("./restaurant/Factures/facture.js")),
    submodules: [
      {
        id: "factureInfo",
        label: "Info facture",
        path: "info/:id",
        component: lazy(() => import("./restaurant/Factures/factureInfo.js")),
      },
    ],
  },
  commandes: {
    id: "commandes",
    label: "Commandes",
    path: "cd",
    icon: "cart4",
    component: lazy(() => import("./restaurant/Commandes/commandesPage.js")),
  },
  reservations: {
    id: "reservations",
    label: "Reservation",
    path: "rs",
    icon: "calendar2-heart",
    component: lazy(() => import("./restaurant/Reservations/commandesPage.js")),
  },
  recette: {
    id: "recette",
    label: "Recette",
    path: "rt",
    icon: "coin",
    component: lazy(() => import("./restaurant/Recette/recette.js")),
  },

   //magazin 
  caisses_magazin: {
    id: "caisses_magazin",
    label: "Caisses",
    path: "ca",
    icon: "pc-display-horizontal",
    component: lazy(() => import("./magazin/Caisse/caisseSimple.js")),
    submodules : [
        {
          id: "caisses",
          label: "Caisse Normale",
          path: "normale",
          icon: "pc-display-horizontal",
          component: lazy(() => import("./magazin/Caisse/caisseSimple.js")),
        },
        {
          id: "caisses",
          label: "Caisse Stock",
          path: "stock",
          icon: "box2-heart-fill",
          component: lazy(() => import("./magazin/Caisse/caisseStock.js")),
        },
        
    ]
  },

  //transporteur
  commande_transporteur: {
    id: "commande_transporteur",
    label: "Commandes",
    path: "cmd",
    icon: "cart4",
    component: lazy(() => import("./transporteur/Commandes/commandeListe.js")),
  },
  stock_transporteur: {
    id: "stock_transporteur",
    label: "Stock",
    path: "sk",
    icon: "box2-heart-fill",
    component: lazy(() => import("./transporteur/Stock/stockPage.js")),
  },
  retour: {
    id: "retour",
    label: "Retour",
    path: "rtr",
    icon: "arrow-repeat",
    component: lazy(() => import("./transporteur/Retour/retourPage.js")),
  },



  // used
  parametre: {
    id: "parametre",
    label: "Parametre",
    path: "st",
    icon: "💰",
    component: lazy(() => import("./Used/settingPage.js")),
    submodules: [
      {
        id: "factureInfo",
        label: "Info facture",
        path: "paymment",
        component: lazy(() => import("./Used/Setting/paymment.js")),
      },
    ],
  },
  update: {
    id: "update",
    label: "update",
    path: "up",
    icon: "💰",
    component: lazy(() => import("./Used/updatePage.js")),
  },
};

/**
 * Resolves a list of module ids (as returned by the backend) into
 * registry entries, silently ignoring any id the frontend doesn't
 * know about (defensive — a backend could ship a module id before
 * the frontend build that supports it is deployed).
 */
export function resolveModules(moduleIds = []) {
  return moduleIds.map((id) => MODULE_REGISTRY[id]).filter(Boolean);
}
